import { useRef, useState } from "react";
import { type useTranslate } from "@refinedev/core";

import { CeIcon } from "./CeIcon";
import type { RawPrintHandle } from "./RawPrintCanvas";
import type { KopVoetSoort } from "./rawPrintLagen";

const KOPVOET_SOORTEN: KopVoetSoort[] = ["kop", "kopEerste", "voet"];

// De werkbalk boven een afdrukdocument met vaste opmaak. Dezelfde twee rijen als in de bron, maar de
// knoppen sturen het document rechtstreeks aan (document.execCommand in het iframe) in plaats van de
// tekstverwerker: die bestaat hier niet, want hij zou de vaste opmaak platslaan.
//
// Waarom niet één werkbalk voor beide: de TipTap-versie leest zijn actieve staat uit de editor
// (staat de cursor in vet?) en heeft uitklappers die op TipTap-commando's leunen. Hier is dat
// allemaal het browsercommando zelf. Twee kleine componenten zijn eerlijker dan één met overal een
// vertakking erin.

type Translate = ReturnType<typeof useTranslate>;

export function RawPrintToolbar({
  canvas,
  t,
  disabled,
}: {
  /** Het document; null zolang het iframe nog niet klaar is. */
  canvas: React.RefObject<RawPrintHandle | null>;
  t: Translate;
  disabled?: boolean;
}) {
  const bestandRef = useRef<HTMLInputElement>(null);
  const [toonLink, setToonLink] = useState(false);
  const [url, setUrl] = useState("");
  const [toonKopVoet, setToonKopVoet] = useState(false);
  const [aanwezig, setAanwezig] = useState<Record<KopVoetSoort, boolean> | null>(null);

  const exec = (commando: string, waarde?: string) => canvas.current?.exec(commando, waarde);
  const voegIn = (html: string) => canvas.current?.insertHtml(html);

  function kiesAfbeelding(bestand: File) {
    const lezer = new FileReader();
    lezer.onload = () => {
      if (typeof lezer.result === "string") {
        voegIn(`<img src="${lezer.result}" alt="" style="max-width:100%">`);
      }
    };
    lezer.readAsDataURL(bestand);
  }

  const Knop = ({
    commando,
    waarde,
    titel,
    children,
  }: {
    commando: string;
    waarde?: string;
    titel: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      className="ce-tb"
      title={titel}
      aria-label={titel}
      disabled={disabled}
      // De knoppen staan buiten het iframe; zonder dit verliest het document zijn selectie bij de
      // muisklik en landt het commando nergens.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(commando, waarde)}
    >
      {children}
    </button>
  );

  const Tegel = ({
    icoon,
    label,
    titel,
    onClick,
  }: {
    icoon: string;
    label: string;
    /** De uitleg in de tooltip; het label onder het icoon is één woord. */
    titel?: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      className="ce-tb2"
      title={titel ?? label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <CeIcon naam={icoon} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="ce-toolbar" role="toolbar" aria-label={t("communicatieEditor.toolbar.label")}>
      <div className="ce-trow ce-trow-fmt">
        <span className="ce-tg">
          <Knop commando="undo" titel={t("communicatieEditor.toolbar.undo")}>
            <CeIcon naam="undo" />
          </Knop>
          <Knop commando="redo" titel={t("communicatieEditor.toolbar.redo")}>
            <CeIcon naam="redo" />
          </Knop>
        </span>
        <span className="ce-tg">
          <Knop commando="bold" titel={t("communicatieEditor.toolbar.bold")}>
            <b>B</b>
          </Knop>
          <Knop commando="italic" titel={t("communicatieEditor.toolbar.italic")}>
            <i>I</i>
          </Knop>
          <Knop commando="underline" titel={t("communicatieEditor.toolbar.underline")}>
            <span style={{ textDecoration: "underline" }}>U</span>
          </Knop>
        </span>
        <span className="ce-tg">
          <Knop commando="formatBlock" waarde="h1" titel={t("communicatieEditor.toolbar.h1")}>
            <span className="ce-tb-txt">H1</span>
          </Knop>
          <Knop commando="formatBlock" waarde="h2" titel={t("communicatieEditor.toolbar.h2")}>
            <span className="ce-tb-txt">H2</span>
          </Knop>
        </span>
        <span className="ce-tg">
          <span className="ce-selbox">
            <select
              className="ce-tsel"
              title={t("communicatieEditor.toolbar.fontFamily")}
              aria-label={t("communicatieEditor.toolbar.fontFamily")}
              disabled={disabled}
              defaultValue=""
              onChange={(e) => e.target.value && exec("fontName", e.target.value)}
            >
              <option value="">{t("communicatieEditor.toolbar.fontFamily")}</option>
              <option>Inter</option>
              <option>Arial</option>
              <option>Georgia</option>
              <option>Times New Roman</option>
            </select>
            <CeIcon naam="chevdown" />
          </span>
        </span>
        <span className="ce-tg">
          <label className="ce-tb" title={t("communicatieEditor.toolbar.color")}>
            <CeIcon naam="color" />
            <input
              type="color"
              className="comm-editor__tb-color"
              aria-label={t("communicatieEditor.toolbar.color")}
              disabled={disabled}
              onChange={(e) => exec("foreColor", e.target.value)}
            />
          </label>
          {/* Arcering, zoals in Word: de cellen in de selectie, of het blok van de cursor. Ook de
              gele balken en labels uit de afdrukstijl gaan zo van kleur, of helemaal zonder. */}
          <label className="ce-tb" title={t("communicatieEditor.toolbar.fill")}>
            <CeIcon naam="fill" />
            <input
              type="color"
              className="comm-editor__tb-color"
              aria-label={t("communicatieEditor.toolbar.fill")}
              disabled={disabled}
              defaultValue="#dede00"
              onChange={(e) => canvas.current?.achtergrond(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="ce-tb"
            title={t("communicatieEditor.toolbar.noFill")}
            aria-label={t("communicatieEditor.toolbar.noFill")}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => canvas.current?.achtergrond(null)}
          >
            <CeIcon naam="nofill" />
          </button>
        </span>
        <span className="ce-tg">
          {/* Inspringen verschuift het hele blok — de totalen, een tabel, een alinea — een tabstop. */}
          <button
            type="button"
            className="ce-tb"
            title={t("communicatieEditor.toolbar.outdent")}
            aria-label={t("communicatieEditor.toolbar.outdent")}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => canvas.current?.inspringen(-1)}
          >
            <CeIcon naam="outdent" />
          </button>
          <button
            type="button"
            className="ce-tb"
            title={t("communicatieEditor.toolbar.indent")}
            aria-label={t("communicatieEditor.toolbar.indent")}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => canvas.current?.inspringen(1)}
          >
            <CeIcon naam="indent" />
          </button>
        </span>
        <span className="ce-tg">
          <Knop commando="justifyLeft" titel={t("communicatieEditor.toolbar.alignLeft")}>
            <CeIcon naam="alignLeft" />
          </Knop>
          <Knop commando="justifyCenter" titel={t("communicatieEditor.toolbar.alignCenter")}>
            <CeIcon naam="alignCenter" />
          </Knop>
          <Knop commando="justifyRight" titel={t("communicatieEditor.toolbar.alignRight")}>
            <CeIcon naam="alignRight" />
          </Knop>
          <Knop commando="justifyFull" titel={t("communicatieEditor.toolbar.alignJustify")}>
            <CeIcon naam="alignJustify" />
          </Knop>
        </span>
      </div>

      <div className="ce-trow ce-trow-insert">
        <span className="comm-editor__tb-pop-wrap">
          <Tegel
            icoon="link"
            label={t("communicatieEditor.tegel.link")}
        titel={t("communicatieEditor.toolbar.link")}
            onClick={() => setToonLink((v) => !v)}
          />
          {toonLink && (
            <div className="comm-editor__tb-pop" role="dialog">
              <label className="comm-editor__tb-pop-lbl" htmlFor="raw-link-url">
                {t("communicatieEditor.link.urlLabel")}
              </label>
              <input
                id="raw-link-url"
                className="comm-editor__tb-pop-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <button
                type="button"
                className="comm-editor__tb-pop-btn"
                onClick={() => {
                  const schoon = url.trim();
                  if (schoon) voegIn(`<a href="${schoon}" rel="noopener" target="_blank">${schoon}</a>`);
                  setUrl("");
                  setToonLink(false);
                }}
              >
                {t("communicatieEditor.link.apply")}
              </button>
            </div>
          )}
        </span>

        <Tegel
          icoon="image"
          label={t("communicatieEditor.tegel.image")}
        titel={t("communicatieEditor.toolbar.image")}
          onClick={() => bestandRef.current?.click()}
        />
        <input
          ref={bestandRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) kiesAfbeelding(f);
            e.target.value = "";
          }}
        />

        <Tegel
          icoon="table"
          label={t("communicatieEditor.tegel.table")}
        titel={t("communicatieEditor.toolbar.table")}
          onClick={() =>
            voegIn(
              '<table style="width:100%;border-collapse:collapse"><tr>'
                + '<td style="border:1px solid #666;padding:4px">&nbsp;</td>'
                + '<td style="border:1px solid #666;padding:4px">&nbsp;</td></tr><tr>'
                + '<td style="border:1px solid #666;padding:4px">&nbsp;</td>'
                + '<td style="border:1px solid #666;padding:4px">&nbsp;</td></tr></table>',
            )
          }
        />
        <Tegel
          icoon="sign"
          label={t("communicatieEditor.tegel.signature")}
        titel={t("communicatieEditor.toolbar.signature")}
          onClick={() => voegIn('<div data-signature="1" style="height:60px;border-bottom:1px solid #666"></div>')}
        />
        <Tegel
          icoon="qr"
          label={t("communicatieEditor.tegel.qr")}
          titel={t("communicatieEditor.toolbar.qr")}
          onClick={() => voegIn('<div data-qr="1">{Factuur.Betaallink}</div>')}
        />
        <Tegel icoon="rule" label={t("communicatieEditor.tegel.hr")}
        titel={t("communicatieEditor.toolbar.hr")} onClick={() => voegIn("<hr>")} />
        <Tegel
          icoon="pagebreak"
          label={t("communicatieEditor.tegel.pageBreak")}
        titel={t("communicatieEditor.toolbar.pageBreak")}
          onClick={() => voegIn('<div style="page-break-after:always"></div>')}
        />

        {/* Kop- en voettekst horen bij de pagina, zoals in Word: ze staan in de paginamarge, komen op elke
            pagina terug, en pagina 1 mag een eigen koptekst hebben. */}
        <span className="comm-editor__tb-pop-wrap">
          <Tegel
            icoon="kopvoet"
            label={t("communicatieEditor.tegel.kopVoet")}
            titel={t("communicatieEditor.toolbar.kopVoet")}
            onClick={() => {
              setAanwezig(canvas.current?.kopVoetAanwezig() ?? null);
              setToonKopVoet((v) => !v);
            }}
          />
          {toonKopVoet && (
            <div className="comm-editor__tb-pop comm-editor__tb-pop--kopvoet" role="dialog"
              aria-label={t("communicatieEditor.toolbar.kopVoet")}>
              {KOPVOET_SOORTEN.map((soort) => {
                const heeft = !!aanwezig?.[soort];
                return (
                  <div key={soort} className="comm-editor__kopvoet-rij">
                    <span className="comm-editor__kopvoet-naam">{t(`communicatieEditor.kopVoet.${soort}`)}</span>
                    <button
                      type="button"
                      className="comm-editor__tb-pop-btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        canvas.current?.kopVoet(soort);
                        setToonKopVoet(false);
                      }}
                    >
                      {t(heeft ? "communicatieEditor.kopVoet.edit" : "communicatieEditor.kopVoet.add")}
                    </button>
                    {heeft && (
                      <button
                        type="button"
                        className="comm-editor__tb-pop-btn is-danger"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          canvas.current?.verwijderKopVoet(soort);
                          setAanwezig(canvas.current?.kopVoetAanwezig() ?? null);
                        }}
                      >
                        {t("communicatieEditor.kopVoet.remove")}
                      </button>
                    )}
                  </div>
                );
              })}
              <p className="comm-editor__kopvoet-uitleg">{t("communicatieEditor.kopVoet.hint")}</p>
            </div>
          )}
        </span>
      </div>
    </div>
  );
}
